import { Loader2 } from 'lucide-react'
import { useLanguageLabel } from '@/shared/hooks/use-language-label'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { LanguagePillSwitcher } from '@/shared/ui/language-pill-switcher'
import { Textarea } from '@/shared/ui/textarea'

export interface LocalizedEditorFieldConfig<TField extends string> {
  /** Key dùng để đọc/ghi giá trị field trong valuesByLanguage. */
  key: TField
  /** Nhãn hiển thị phía trên input hoặc textarea. */
  label: string
  /** Placeholder hiển thị trong input hoặc textarea. */
  placeholder: string
  /** Bật textarea thay vì input một dòng. */
  multiline?: boolean
  /** Số dòng của textarea khi multiline được bật. */
  rows?: number
  /** Field được tính vào trạng thái hoàn thành/validate dịch. Mặc định là bắt buộc. */
  required?: boolean
}

export interface LocalizedFieldsEditorProps<TField extends string> {
  /** Tiêu đề tùy chọn của block localized fields. */
  title?: string
  /** Danh sách ngôn ngữ hiển thị trong language switcher. */
  languages: string[]
  /** Ngôn ngữ đang được chọn, state này do component cha quản lý. */
  activeLanguage: string
  /** Hàm được gọi khi user chọn một ngôn ngữ khác. */
  onSelectLanguage: (language: string) => void
  /** Danh sách ngôn ngữ được đánh dấu là đã hoàn thành. */
  completedLanguages?: string[]
  /** Giá trị các field được gom theo mã ngôn ngữ. */
  valuesByLanguage: Record<string, Record<TField, string>>
  /** Hàm được gọi khi user sửa field; component cha chịu trách nhiệm cập nhật state. */
  onChangeField: (language: string, field: TField, value: string) => void
  /** Cấu hình các field cần render để editor dùng lại được cho nhiều entity. */
  fields: LocalizedEditorFieldConfig<TField>[]
  /** Panel dịch tùy chọn; component cha quyết định flow gọi API/job phía sau. */
  translatePanel?: {
    /** Bật/tắt hiển thị panel dịch. */
    visible: boolean
    /** Danh sách ngôn ngữ có thể chọn làm output. */
    languages: string[]
    /** Danh sách ngôn ngữ output đang được chọn. */
    selectedLanguages: string[]
    /** Hàm được gọi khi user chọn/bỏ chọn ngôn ngữ output. */
    onSelectedLanguagesChange: (languages: string[]) => void
    /** Hàm được gọi khi user bấm nút dịch. */
    onTranslate: () => void
    /** Nhãn của nút dịch. */
    translateLabel: string
    /** Mô tả ngắn ở đầu panel dịch. */
    scopeHint: string
    /** Nhãn checkbox chọn tất cả. */
    selectAllLabel: string
    /** Nhãn dùng để đánh dấu ngôn ngữ hiện tại. */
    currentLabel: string
    /** Nội dung loading tùy chọn khi component cha đang dịch. */
    loadingText?: string
    /** Trạng thái loading do component cha truyền xuống. */
    isTranslating?: boolean
  }
  /** Khóa toàn bộ field nhập liệu khi true. */
  isFieldReadOnly?: boolean
  /** Dòng giải thích hiển thị khi field đang bị khóa. */
  readOnlyHint?: string
}

const EMPTY_COMPLETED_LANGUAGES: string[] = []

/**
 * Editor nhập liệu theo nhiều ngôn ngữ.
 *
 * Component này chỉ xử lý phần hiển thị: tab ngôn ngữ, các input/textarea theo cấu hình field,
 * trạng thái readonly và panel chọn ngôn ngữ để dịch. State dữ liệu, active language và flow gọi
 * API/job dịch đều do component cha quản lý thông qua props callback.
 */
export function LocalizedFieldsEditor<TField extends string>({
  title,
  languages,
  activeLanguage,
  onSelectLanguage,
  completedLanguages = EMPTY_COMPLETED_LANGUAGES,
  valuesByLanguage,
  onChangeField,
  fields,
  translatePanel,
  isFieldReadOnly = false,
  readOnlyHint,
}: LocalizedFieldsEditorProps<TField>) {
  const languageLabel = useLanguageLabel()
  const languageLabels = Object.fromEntries(
    languages.map((language) => {
      return [language, languageLabel(language)]
    }),
  )
  const selectedLanguages = translatePanel?.selectedLanguages ?? []

  const allLanguagesSelected =
    (translatePanel?.languages.length ?? 0) > 0 &&
    (translatePanel?.languages ?? []).every((language) => {
      return selectedLanguages.includes(language)
    })

  return (
    <div className="space-y-4 rounded-xl border border-zinc-200 p-4">
      {title && <p className="text-sm font-semibold text-zinc-900">{title}</p>}
      <LanguagePillSwitcher
        languages={languages}
        activeLanguage={activeLanguage}
        onSelect={onSelectLanguage}
        completedLanguages={completedLanguages}
        languageLabels={languageLabels}
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {fields.map((field) => {
          const value = valuesByLanguage[activeLanguage]?.[field.key] ?? ''
          if (field.multiline) {
            return (
              <FormField key={field.key} label={field.label} required={field.required !== false}>
                <Textarea
                  rows={field.rows ?? 3}
                  className="resize-none"
                  value={value}
                  disabled={isFieldReadOnly}
                  placeholder={field.placeholder}
                  onChange={(event) => {
                    onChangeField(activeLanguage, field.key, event.target.value)
                  }}
                />
              </FormField>
            )
          }
          return (
            <FormField key={field.key} label={field.label} required={field.required !== false}>
              <Input
                value={value}
                disabled={isFieldReadOnly}
                placeholder={field.placeholder}
                onChange={(event) => {
                  onChangeField(activeLanguage, field.key, event.target.value)
                }}
              />
            </FormField>
          )
        })}
      </div>
      {isFieldReadOnly && readOnlyHint && <p className="text-xs text-zinc-600">{readOnlyHint}</p>}
      {translatePanel?.visible && (
        <div className="space-y-3 rounded-xl border border-blue-200 bg-blue-50/70 p-3">
          <p className="text-xs font-medium text-blue-700">{translatePanel.scopeHint}</p>
          <div className="grid gap-2 rounded-lg border border-blue-100 bg-white p-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-zinc-700">
              <Checkbox
                checked={allLanguagesSelected}
                onCheckedChange={(checked) => {
                  if (checked === true) {
                    translatePanel.onSelectedLanguagesChange(translatePanel.languages)
                    return
                  }
                  translatePanel.onSelectedLanguagesChange([])
                }}
              />
              {translatePanel.selectAllLabel}
            </label>
            <div className="grid gap-2 sm:grid-cols-2">
              {translatePanel.languages.map((language) => {
                const isActive = language === activeLanguage
                const isChecked = selectedLanguages.includes(language)
                return (
                  <label key={language} className="flex items-center gap-2 text-sm text-zinc-700">
                    <Checkbox
                      checked={isChecked}
                      disabled={isActive}
                      onCheckedChange={(checked) => {
                        if (isActive) {
                          return
                        }
                        if (checked === true) {
                          translatePanel.onSelectedLanguagesChange(
                            Array.from(new Set([...selectedLanguages, language])),
                          )
                          return
                        }
                        translatePanel.onSelectedLanguagesChange(
                          selectedLanguages.filter((item) => {
                            return item !== language
                          }),
                        )
                      }}
                    />
                    <span>{languageLabels[language] ?? language.toUpperCase()}</span>
                    {isActive && (
                      <span className="text-xs text-zinc-500">{`(${translatePanel.currentLabel})`}</span>
                    )}
                  </label>
                )
              })}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="bg-blue-600 text-white hover:bg-blue-700"
              disabled={(translatePanel.isTranslating ?? false) || selectedLanguages.length < 1}
              onClick={translatePanel.onTranslate}
            >
              {translatePanel.translateLabel}
            </Button>
          </div>
          {translatePanel.isTranslating && (
            <p className="flex items-center gap-2 text-xs text-zinc-600">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {translatePanel.loadingText ?? ''}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
